import React from 'react';
import ReactCountryFlag from 'react-country-flag';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';

export function countryFromPhone(phone) {
    if (!phone) return null;
    const raw = String(phone).trim();
    const normalized = raw.startsWith('+') ? raw : `+${raw}`;
    try {
        const parsed = parsePhoneNumberFromString(normalized);
        return parsed && parsed.country ? parsed.country : null;
    } catch (e) {
        return null;
    }
}

export function PhoneFlag({ phone, size = '1.2em', style }) {
    const iso = countryFromPhone(phone);
    if (!iso) return null;
    return (
        <ReactCountryFlag
            countryCode={iso}
            svg
            title={iso}
            style={{ fontSize: size, lineHeight: 1, ...style }}
        />
    );
}

export function ILFlag({ size = '1.2em', title = 'Israël (IL)' }) {
    return (
        <span title={title} style={{ marginLeft: 6 }}>
            <ReactCountryFlag
                countryCode="IL"
                svg
                title="IL"
                style={{ fontSize: size, lineHeight: 1 }}
            />
        </span>
    );
}

// Dispatch pool a doctor/patient/request belongs to. Mirrors POOLS in
// feeli-server/server/utils/pools.js, which owns the concept.
export const DEFAULT_POOL = 'FR';
const POOL_FLAG = { IL: 'IL', ES: 'ES' };
const POOL_TITLE = { IL: 'Israël (IL)', ES: 'Espagne (ES)' };

// The pool of a record that may predate the field, mirroring poolOf() on the
// server: an explicit `pool` wins, otherwise fall back to the legacy boolean.
export function poolOf(record) {
    if (!record) return DEFAULT_POOL;
    if (record.pool) return String(record.pool).trim().toUpperCase();
    return record.isIL ? 'IL' : DEFAULT_POOL;
}

// Markets offered in the requests filter. IL is staff-only — see the role gate in
// RequestsController — so callers decide whether to offer it.
export const MARKET_FILTER_OPTIONS = [
    { value: 'IL', label: 'Israël (IL)', staffOnly: true },
    { value: 'ES', label: 'Espagne (ES)', staffOnly: false },
];

/**
 * Market marker for a REQUEST row — text, not a flag, and deliberately so.
 *
 * The rows already carry LangFlag, which renders 🇪🇸 for a patient whose account
 * language is Spanish. A market flag would render the same 🇪🇸 for a patient who
 * declared Spain, and the two are NOT the same fact: language decides what they
 * are written to in, the market decides which doctors may treat them. A Spanish
 * patient reading the Spanish site is both, and would have shown two identical
 * flags side by side saying nothing.
 *
 * So the market reads as a labelled code and the language keeps the flag. An
 * unknown market shows in red rather than being dropped, matching PoolFlag: a
 * value this build does not recognise is exactly what somebody needs to see.
 *
 * PoolFlag stays as it is for the doctors list, where there is no language badge
 * beside it and a flag is the clearer thing.
 */
export function MarketBadge({ market }) {
    const resolved = market ? String(market).trim().toUpperCase() : DEFAULT_POOL;
    if (!resolved || resolved === DEFAULT_POOL) return null;
    const known = POOL_TITLE[resolved];
    return (
        <span
            title={known ? `Marché : ${known}` : `Marché inconnu : ${resolved}`}
            style={{
                marginLeft: 6,
                padding: '0 4px',
                borderRadius: 3,
                border: `1px solid ${known ? '#8a97a3' : '#b94a48'}`,
                color: known ? '#55636f' : '#b94a48',
                fontSize: '0.72em',
                fontWeight: 700,
                letterSpacing: '0.03em',
                verticalAlign: 'middle',
                whiteSpace: 'nowrap',
            }}
        >
            {resolved}
        </span>
    );
}

/**
 * The market a REQUEST belongs to, for badging the list.
 *
 * Deliberately wider than poolOf(request): it reads the patient too, and has to
 * agree with the server filter or the list badges a different set than it
 * returned. The request's own snapshot is not sufficient on its own —
 *   IL: requests created before feeli-server stamped isIL carry nothing;
 *   ES: a Spanish patient may sit in the DEFAULT pool, either because no ES
 *       doctor was active when they signed up (poolStaffing diverts them) or
 *       because they predate ES routing and were deliberately left where they
 *       were.
 *
 * IL is tested first: it is the older, stricter partition, and where a record
 * carries both signals the more restrictive market should win the badge.
 */
export function marketOfRequest(request) {
    if (!request) return DEFAULT_POOL;
    const user = request.userId || {};
    const norm = (v) => (v ? String(v).trim().toUpperCase() : '');

    if (norm(request.pool) === 'IL' || request.isIL
        || norm(user.pool) === 'IL' || user.isIL
        || countryFromPhone(user.phone) === 'IL') {
        return 'IL';
    }
    if (norm(request.pool) === 'ES' || norm(user.pool) === 'ES' || norm(user.country) === 'ES') {
        return 'ES';
    }
    return DEFAULT_POOL;
}

/**
 * The market a PATIENT belongs to, mirroring the users-list country buckets that
 * buildCountryPhoneFilter owns on the server. It has to agree with them, or the
 * list badges a different set than the filter returned.
 *
 * Both non-default markets are DECLARED — the patient's record says so whatever
 * number they typed — so neither can be read off the phone alone:
 *   IL: `isIL`, derived upstream from the declared country;
 *   ES: the dispatch `pool`, or the declared `country`. The pool alone is not
 *       enough — a Spanish patient sits in the DEFAULT pool whenever no ES doctor
 *       was active at signup, or when they predate ES routing entirely.
 *
 * IL is tested first, as in marketOfRequest: where a record carries both signals
 * the older, stricter partition wins.
 */
export function marketOfUser(user) {
    if (!user) return DEFAULT_POOL;
    const norm = (v) => (v ? String(v).trim().toUpperCase() : '');
    // Leg for leg with ilMatch()/esMatch() on the server — including the phone legs,
    // and NOT including a `pool: 'IL'` leg the server bucket does not have. Any leg
    // this side has that the server lacks SUPPRESSES the ES badge on a row the ES
    // filter returned, which is the one failure the badge exists to prevent.
    // (feeli-server asserts pool === 'IL' ⟺ isIL, so the flag already covers it.)
    if (user.isIL || countryFromPhone(user.phone) === 'IL') return 'IL';
    if (norm(user.pool) === 'ES' || norm(user.country) === 'ES'
        || countryFromPhone(user.phone) === 'ES') return 'ES';
    return DEFAULT_POOL;
}

/**
 * Flag for a NON-DEFAULT pool.
 *
 * Renders nothing for FR on purpose. FR is not "France", it is the default
 * market that holds ~1135 of the 1137 doctors, and flagging all of them would
 * turn a signal into wallpaper — the existing IL flag worked precisely because
 * it marked the exception. Only markets that were deliberately partitioned out
 * get a flag.
 *
 * An unrecognised pool renders its code as text rather than nothing: a value
 * this build does not know is exactly what someone needs to see, and silently
 * showing it as un-flagged would make it look like an ordinary FR doctor.
 */
export function PoolFlag({ record, pool, size = '1.2em', label }) {
    const resolved = pool ? String(pool).trim().toUpperCase() : poolOf(record);
    if (!resolved || resolved === DEFAULT_POOL) return null;
    const iso = POOL_FLAG[resolved];
    if (!iso) {
        return (
            <span
                title={`Pool inconnu : ${resolved}`}
                style={{ marginLeft: 6, fontSize: '0.8em', fontWeight: 700, color: '#b94a48' }}
            >
                {resolved}
            </span>
        );
    }
    return (
        <span title={label || POOL_TITLE[resolved] || resolved} style={{ marginLeft: 6 }}>
            <ReactCountryFlag
                countryCode={iso}
                svg
                title={iso}
                style={{ fontSize: size, lineHeight: 1 }}
            />
        </span>
    );
}
